import { useMemo } from 'react';
import { Button, Form, Modal, Select, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type { FieldDefinitionDto } from '@crm/shared';
import { DynamicInput, toApiValue } from './DynamicField';
import { SYSTEM_PROPS } from '../features/fields/system-keys';
import { plural } from '../utils/format';

/** System fields the bulk update endpoint accepts (emails/phones are unique per contact, the rest are read-only). */
const BULK_SYSTEM_KEYS = new Set(['first_name', 'last_name', 'job_title', 'organization_id', 'lifecycle_stage', 'lead_status', 'source', 'subscribed']);

interface Row {
  key?: string;
  value?: unknown;
}

interface Props {
  open: boolean;
  count: number;
  contactFields: FieldDefinitionDto[];
  loading: boolean;
  onCancel: () => void;
  /** Receives the bulk update payload: system props at the top level, custom fields under `fields`. */
  onApply: (data: Record<string, unknown>) => void;
}

/** Picks one or more contact fields and a value for each, then sets them on every selected contact. */
export function MassEditModal({ open, count, contactFields, loading, onCancel, onApply }: Props) {
  const [form] = Form.useForm<{ rows: Row[] }>();
  const people = plural(count, 'contact');
  const editable = useMemo(() => contactFields.filter((d) => (d.isSystem ? BULK_SYSTEM_KEYS.has(d.key) : true)), [contactFields]);
  const byKey = useMemo(() => new Map(editable.map((d) => [d.key, d])), [editable]);
  const rows = Form.useWatch('rows', form) ?? [];
  const picked = new Set(rows.map((r) => r?.key).filter(Boolean));

  const options = useMemo(() => {
    const groups = new Map<string, FieldDefinitionDto[]>();
    for (const d of editable) {
      const g = d.group ?? 'Other';
      groups.set(g, [...(groups.get(g) ?? []), d]);
    }
    return [...groups].map(([group, defs]) => ({ label: group, title: group, options: defs.map((d) => ({ value: d.key, label: d.label })) }));
  }, [editable]);

  const submit = ({ rows: values }: { rows: Row[] }) => {
    const data: Record<string, unknown> = {};
    const fields: Record<string, unknown> = {};
    for (const row of values ?? []) {
      const def = row?.key ? byKey.get(row.key) : undefined;
      if (!def) continue;
      let v = toApiValue(def, row.value);
      if (Array.isArray(v) && !v.length) v = null;
      if (def.isSystem) data[SYSTEM_PROPS.CONTACT[def.key] ?? def.key] = v;
      else fields[def.key] = v;
    }
    if (Object.keys(fields).length) data.fields = fields;
    if (Object.keys(data).length) onApply(data);
  };

  return (
    <Modal
      title={`Edit fields on ${people}`}
      open={open}
      onCancel={onCancel}
      okText={`Apply to ${people}`}
      okButtonProps={{ loading, disabled: !picked.size }}
      onOk={() => form.submit()}
      afterOpenChange={(o) => o && form.resetFields()}
      destroyOnHidden
      width={600}
    >
      <Typography.Paragraph type="secondary">Pick the fields to change. Every selected contact gets the same value; other fields stay as they are.</Typography.Paragraph>
      <Form form={form} layout="vertical" onFinish={submit} initialValues={{ rows: [{}] }}>
        <Form.List name="rows">
          {(items, { add, remove }) => (
            <>
              {items.map(({ key, name }) => {
                const def = rows[name]?.key ? byKey.get(rows[name].key as string) : undefined;
                const blank = def && def.type !== 'BOOLEAN' && toApiValue(def, rows[name]?.value) === null;
                return (
                  <div key={key} style={{ display: 'grid', gridTemplateColumns: '200px minmax(0, 1fr) 32px', gap: 8, alignItems: 'start' }}>
                    <Form.Item name={[name, 'key']} rules={[{ required: true, message: 'Choose a field' }]}>
                      <Select
                        showSearch
                        placeholder="Choose a field"
                        options={options.map((g) => ({ ...g, options: g.options.map((o) => ({ ...o, disabled: o.value !== rows[name]?.key && picked.has(o.value) })) }))}
                        filterOption={(input, o) => String(o?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                        onChange={() => form.setFieldValue(['rows', name, 'value'], undefined)}
                      />
                    </Form.Item>
                    {def ? (
                      <Form.Item
                        key={def.key}
                        name={[name, 'value']}
                        valuePropName={def.type === 'BOOLEAN' ? 'checked' : 'value'}
                        rules={def.type === 'EMAIL' ? [{ type: 'email', message: 'Invalid email' }] : undefined}
                        extra={blank ? `Left empty, this clears ${def.label} on all ${people}.` : undefined}
                      >
                        <DynamicInput def={def} placeholder="New value" />
                      </Form.Item>
                    ) : (
                      <div />
                    )}
                    <Button type="text" icon={<DeleteOutlined />} aria-label="Remove field" disabled={items.length === 1} onClick={() => remove(name)} />
                  </div>
                );
              })}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})} disabled={picked.size >= editable.length} block>
                Add another field
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
}
